import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  acceptOrderThunk,
  fetchOrderPrintDetails,
  finishOrderThunk,
  selectAllStatusOrders,
  setShowOrderInfosModal,
  updateStatusOrders,
} from "../../store/actions/ordersSlice";
import OrdersRow from "../orders/table/ordersRow";
import LoadingBox from "../indicators/LoadingBox";
import DashboardNoOrdersMessage from "./DashboardNoOrdersMessage";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Slide } from "@mui/material";
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { ERROR_QZ_TRAY_REQUIRED } from "../../common/helpersTxt";
import { setCenter } from "../../store/actions/mapSlice";
import { extraDelivery } from "../../api/order";
import ExtraDeliveryDialog from "../dialogs/orders/ExtraDeliveryDialog";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import { NO_DISPATCHED_ORDER_MESSAGE } from "../../types/order";
import CompactOrderCard from "./OrderCardComponent/CompactOrderCard";

const DashboardStatusOrderList = ({ status = "orders-on-way", viewMode = "list" }) => {
  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [selectedDriver, setSelectedDriver] = useState(false);
  const [driverBlockedWarning, setDriverBlockedWarning] = useState(null);
  const [extraDeliveryReason, setExtraDeliveryReason] = useState(null);

  const [driverError, setDriverError] = useState(null);
  const [error, setError] = useState(null);

  const orders = useSelector(selectAllStatusOrders);
  const loadingOrders = useSelector((state) => state.orders.loadingOrders);
  const showOrderInfosModal = useSelector(
    (state) => state.orders.showOrderInfosModal
  );

  const componentRef = useRef();
  const reactToPrintRef = useRef();

  useEffect(() => {
    dispatch(updateStatusOrders({ status }));
  }, [dispatch, status]);

  const handleOrderAction = useCallback(
    (action, order) => {
      dispatch(action({ order }));
    },
    [dispatch]
  );

  const handleOrderModal = useCallback(
    (order) => {
      dispatch(
        setShowOrderInfosModal({
          orderId: order.order_id,
          show: !showOrderInfosModal,
        })
      );
    },
    [dispatch, showOrderInfosModal]
  );

  const printOrder = useCallback(
    async (orderId, e) => {
      try {
        e.stopPropagation();
        await dispatch(fetchOrderPrintDetails({ orderId })).unwrap(); // Set the order details in state
      } catch (error) {
        console.error("Failed to fetch order details:", error);
        dispatchSnackBarMessage(
          `dashboard-status-orders-list-print-${new Date()}`,
          ERROR_QZ_TRAY_REQUIRED,
          "error"
        );
      }
    },
    [dispatch]
  );

  const handleChangeMapCenter = useCallback(
    (lat, lng) => {
      const latNumber = parseFloat(lat);
      const lngNumber = parseFloat(lng);

      dispatch(
        setCenter({
          lat: latNumber,
          lng: lngNumber,
        })
      );
    },
    [dispatch]
  );

  const handleExtraDelivery = useCallback((order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleDialogConfirm = async () => {
    try {
      if (!selectedDriver || selectedDriver === 0) {
        setDriverError("Selecione um motorista válido antes de continuar.");
        return;
      } else {
        setDriverError(null);
      }

      await extraDelivery({
        orderId: selectedOrder.order_id,
        driver_id: selectedDriver,
      });
      handleDialogClose();
    } catch (e) {
      console.error("Error processing extra delivery:", e);
    }
  };

  const validateSelection = (value) => {
    if (!value) {
      setError("Selecione um motorista antes de continuar.");
    } else {
      setError(null);
    }
  };

  const handleSelectChange = useCallback(
    (e, value) => {
      if (value?.isBlocked) {
        setDriverBlockedWarning(
          "Motorista se encontra em uma corrida, deseja adicionar?"
        );
      }

      setDriverBlockedWarning(null);
      validateSelection(value);
      setSelectedDriver(value.value);
    },
    [dispatch]
  );

  // Card Handlers
  const handleAcceptOrder = useCallback(
      (order) => {
          handleOrderAction(acceptOrderThunk, order);
      },
      [handleOrderAction]
  );

  const handlePrintOrder = useCallback(
      async (orderId, e) => {
          await printOrder(orderId, e);
      },
      [printOrder]
  );

  const handleViewDetails = useCallback(
      (order) => {
          handleOrderModal(order);
      },
      [handleOrderModal]
  );

  const OrderRows = useMemo(() => {
    return orders.map((item, index) => (
      <OrdersRow
        key={item.order_id}
        item={item}
        dashboard
        showCenter={false}
        onlyModalAction={false}
        printOrder={printOrder}
        reactToPrintRef={reactToPrintRef}
        componentRef={componentRef}
        changeMapCenter={handleChangeMapCenter}
        finishOrder={() => handleOrderAction(finishOrderThunk, item)}
        handleOrderModal={() => handleOrderModal(item)}
        extraDelivery={() => handleExtraDelivery(item)}
      />
    ));
  }, [
    orders,
    printOrder,
    handleChangeMapCenter,
    handleOrderAction,
    handleOrderModal,
  ]);

  const OrderCards = useMemo(() => {
      return orders.map((order) => (
          <Grid
              item
              xs={12}
              sm={12} // Full width on mobile
              md={12} // Full width on tablet if list view constrained
              lg={12}
              xl={6} // Two columns on very wide screens
              key={order.order_id || order.order_index}
          >
              <CompactOrderCard
                  order={order}
                  onViewDetails={handleViewDetails}
                  onAcceptOrder={handleAcceptOrder}
                  onPrintOrder={handlePrintOrder}
              />
          </Grid>
      ));
  }, [orders, handleViewDetails, handleAcceptOrder, handlePrintOrder]);

  const renderContent = () => {
      if (orders.length === 0) {
          return (
              <DashboardNoOrdersMessage
                  title={"Nenhum pedido despachado"}
                  body={NO_DISPATCHED_ORDER_MESSAGE}
                  icon={<DeliveryDiningIcon />}
              />
          );
      }

      if (viewMode === "grid") {
          return (
              <Grid container spacing={2}>
                  {OrderCards}
              </Grid>
          );
      }

      return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{OrderRows}</Box>;
  };

  if (loadingOrders) {
    return <LoadingBox height="100%" />;
  }

  return (
    <>
      <Slide in={!loadingOrders} direction="up" mountOnEnter unmountOnExit>
        <Box className="order-list-step">
          {renderContent()}
        </Box>
      </Slide>
      <ExtraDeliveryDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        driverError={driverError}
        handleSelectChange={handleSelectChange}
        onConfirm={handleDialogConfirm}
        driverBlockedWarning={driverBlockedWarning}
        reason={extraDeliveryReason}
        setReason={setExtraDeliveryReason}
      />
    </>
  );
};

export default React.memo(DashboardStatusOrderList);
